import { createAction, createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { ApiStatus } from "../models/app/apiStatus";
import {
  AdvertisementRequest,
  Companies,
  ContactRequestForm,
  OrderRequest,
  UpdateDataRequest,
} from "../app/redux/poslovniImenikApi";
import { CompanyModel } from "../models/company/companyModel";
import { RootState } from "../app/redux/store";
import { CompanyFullDetailsModel } from "../models/company/companyFullDetailsModel";
import CategoryResponseModel from "../models/company/categoryResponseModel";
import { BannerModel } from "../models/bannerModel";
import { AdvanceSearchFilterModel } from "../models/advanceSearchFilterModel";
import { CompanySearchResponse } from "../models/company/companySearchResponse";
import { AdvertisementRequestModel } from "../models/advertisementRequestModel";
import ContactRequest from "../models/contactRequest";
import { UpdateDataModel } from "../models/editions/updatedDataModel";
import { AdvertisementModel } from "../models/editions/advertisementModel";
import {
  setErrorNotification,
  setPendingNotification,
  setSuccessNotification,
} from "./notificationSlice";
import { ApiResponse } from "../models/app/apiResponse";
import i18n from "../i18n";

type CompanyState = {
  companySearchResponse: CompanySearchResponse | null;
  companyById: CompanyFullDetailsModel | null;
  categories: CategoryResponseModel[] | null;
  banners: BannerModel[] | null;
  orderRequest: AdvertisementRequestModel | null;
  advertisementRequest: AdvertisementModel | null;
  updateDataRequest: UpdateDataModel | null;
  contactRequest: ContactRequest | null;
  statuses: {
    savedCompany: ApiStatus;
    getCompany: ApiStatus;
    search: ApiStatus;
    orderRequest: ApiStatus;
    advertisementRequest: ApiStatus;
    updateDataRequest: ApiStatus;
    contactRequest: ApiStatus;
  };
};

const initialState: CompanyState = {
  companySearchResponse: null,
  companyById: null,
  categories: null,
  banners: null,
  advertisementRequest: null,
  contactRequest: null,
  orderRequest: null,
  updateDataRequest: null,

  statuses: {
    savedCompany: ApiStatus.Idle,
    getCompany: ApiStatus.Idle,
    search: ApiStatus.Idle,
    orderRequest: ApiStatus.Idle,
    advertisementRequest: ApiStatus.Idle,
    updateDataRequest: ApiStatus.Idle,
    contactRequest: ApiStatus.Idle,
  },
};

export const selectCompanies = (state: RootState) =>
  state.company.companySearchResponse;
export const selectCompaniesPagination = (state: RootState) =>
  state.company.companySearchResponse;
export const selectCompanySearchResponse = (state: RootState) =>
  state.company.companySearchResponse;
export const selectCompanyById = (state: RootState) =>
  state.company.companyById;
export const selectAllCategories = (state: RootState) =>
  state.company.categories;
export const selectAllBanners = (state: RootState) => state.company.banners;
export const selectLoadingFullDetailsStatus = (state: RootState) =>
  state.company.statuses.getCompany;
export const selectSearchStatus = (state: RootState) =>
  state.company.statuses.search;
export const selectAdvertisementStatus = (state: RootState) =>
  state.company.statuses.advertisementRequest;
export const selectUpdateDataStatus = (state: RootState) =>
  state.company.statuses.updateDataRequest;
export const selectOrderStatus = (state: RootState) =>
  state.company.statuses.orderRequest;
export const selectContactStatus = (state: RootState) =>
  state.company.statuses.contactRequest;

export const getCompanies = createAsyncThunk(
  "Company/Get_Companies",
  async (request: AdvanceSearchFilterModel) => {
    const response = await Companies.GetCompanies(request);
    return response.data;
  }
);

export const getCompanyFullDetailsById = createAsyncThunk(
  "Company/Get_Company_Full_Details_By_Id",
  async (id: string) => {
    const response = await Companies.GetCompanyFullDetailsById(id);
    return response.data;
  }
);

export const getAllCategories = createAsyncThunk(
  "Company/Get_All_Categories",
  async () => {
    const response = await Companies.GetAllCategories();
    return response.data;
  }
);

export const getAllBanners = createAsyncThunk(
  "Company/Get_All_Banners",
  async () => {
    const response = await Companies.GetAllBanners();
    return response.data;
  }
);

export const sendOrderRequest = createAsyncThunk<
  AdvertisementRequestModel | null,
  AdvertisementRequestModel,
  { rejectValue: string }
>(
  "Company/Send_Order_Request",
  async (request: AdvertisementRequestModel, { rejectWithValue, dispatch }) => {
    try {
      dispatch(
        setPendingNotification(i18n.t("messageKey.orderRequest_Pending"))
      );
      const response = await OrderRequest.SendOrderRequest(request);
      dispatch(
        setSuccessNotification(i18n.t("messageKey.orderRequest_Success"))
      );
      return response.data;
    } catch (error) {
      const errorResponse = error as ApiResponse<null>;
      dispatch(setErrorNotification(i18n.t("messageKey.orderRequest_Error")));
      return rejectWithValue(errorResponse.messageKey);
    }
  }
);

export const sendUpdateDataRequest = createAsyncThunk<
  UpdateDataModel | null,
  UpdateDataModel,
  { rejectValue: string }
>(
  "Company/Send_Update_Data_Request",
  async (request: UpdateDataModel, { rejectWithValue, dispatch }) => {
    try {
      dispatch(
        setPendingNotification(i18n.t("messageKey.updateDataRequest_Pending"))
      );
      const response = await UpdateDataRequest.SendUpdateDataRequest(request);
      dispatch(
        setSuccessNotification(i18n.t("messageKey.updateDataRequest_Success"))
      );
      return response.data;
    } catch (error) {
      const errorResponse = error as ApiResponse<null>;
      dispatch(
        setErrorNotification(i18n.t("messageKey.updateDataRequest_Error"))
      );
      return rejectWithValue(errorResponse.messageKey);
    }
  }
);

export const sendAdvertisementRequest = createAsyncThunk<
  AdvertisementModel | null,
  AdvertisementModel,
  { rejectValue: string }
>(
  "Company/Send_Advertisement_Request",
  async (request: AdvertisementModel, { rejectWithValue, dispatch }) => {
    try {
      dispatch(
        setPendingNotification(i18n.t("messageKey.updateDataRequest_Pending"))
      );
      const response = await AdvertisementRequest.SendAdvertisementRequest(
        request
      );
      dispatch(
        setSuccessNotification(i18n.t("messageKey.updateDataRequest_Success"))
      );
      return response.data;
    } catch (error) {
      const errorResponse = error as ApiResponse<null>;
      dispatch(
        setErrorNotification(i18n.t("messageKey.advertisementRequest_Error"))
      );
      return rejectWithValue(errorResponse.messageKey);
    }
  }
);

export const sendContactRequest = createAsyncThunk<
  ContactRequest | null,
  ContactRequest,
  { rejectValue: string }
>(
  "Company/Send_Contact_Request",
  async (request: ContactRequest, { rejectWithValue, dispatch }) => {
    try {
      dispatch(
        setPendingNotification(i18n.t("messageKey.contactRequest_Pending"))
      );
      const response = await ContactRequestForm.SendContactRequest(request);
      dispatch(
        setSuccessNotification(i18n.t("messageKey.contactRequest_Success"))
      );
      return response.data;
    } catch (error) {
      const errorResponse = error as ApiResponse<null>;
      dispatch(setErrorNotification(i18n.t("messageKey.contactRequest_Error")));
      return rejectWithValue(errorResponse.messageKey);
    }
  }
);

export const resetSearch = createAction("Company/Reset_Search");
export const resetCompanyById = createAction("Company/Reset_Company_By_Id");
export const resetCompany = createAction("Company/Reset_Company");

const companySlice = createSlice({
  name: "company",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(sendAdvertisementRequest.pending, (state, action) => {
        state.statuses.advertisementRequest = ApiStatus.Pending;
      })
      .addCase(sendAdvertisementRequest.fulfilled, (state, action) => {
        state.statuses.advertisementRequest = ApiStatus.Fulfilled;
      })
      .addCase(sendAdvertisementRequest.rejected, (state, action) => {
        state.statuses.advertisementRequest = ApiStatus.Rejected;
      })

      .addCase(sendUpdateDataRequest.pending, (state, action) => {
        state.statuses.updateDataRequest = ApiStatus.Pending;
      })
      .addCase(sendUpdateDataRequest.fulfilled, (state, action) => {
        state.statuses.updateDataRequest = ApiStatus.Fulfilled;
      })
      .addCase(sendUpdateDataRequest.rejected, (state, action) => {
        state.statuses.updateDataRequest = ApiStatus.Rejected;
      })

      .addCase(sendOrderRequest.pending, (state, action) => {
        state.statuses.orderRequest = ApiStatus.Pending;
      })
      .addCase(sendOrderRequest.fulfilled, (state, action) => {
        state.statuses.orderRequest = ApiStatus.Fulfilled;
      })
      .addCase(sendOrderRequest.rejected, (state, action) => {
        state.statuses.orderRequest = ApiStatus.Rejected;
      })

      .addCase(sendContactRequest.pending, (state, action) => {
        state.statuses.contactRequest = ApiStatus.Pending;
      })
      .addCase(sendContactRequest.fulfilled, (state, action) => {
        state.statuses.contactRequest = ApiStatus.Fulfilled;
      })
      .addCase(sendContactRequest.rejected, (state, action) => {
        state.statuses.contactRequest = ApiStatus.Rejected;
      })

      .addCase(getCompanies.pending, (state, action) => {
        state.statuses.search = ApiStatus.Pending;
      })
      .addCase(getCompanies.fulfilled, (state, action) => {
        state.companySearchResponse = action.payload;
        state.statuses.search = ApiStatus.Fulfilled;
      })
      .addCase(getCompanies.rejected, (state, action) => {
        state.statuses.search = ApiStatus.Rejected;
      })
      .addCase(getCompanyFullDetailsById.fulfilled, (state, action) => {
        state.companyById = action.payload;
        state.statuses.getCompany = ApiStatus.Fulfilled;
      })
      .addCase(getCompanyFullDetailsById.pending, (state, action) => {
        state.statuses.getCompany = ApiStatus.Pending;
      })
      .addCase(getCompanyFullDetailsById.rejected, (state, action) => {
        state.statuses.getCompany = ApiStatus.Rejected;
      })
      .addCase(getAllCategories.fulfilled, (state, action) => {
        state.categories = action.payload;
      })
      .addCase(getAllBanners.fulfilled, (state, action) => {
        state.banners = action.payload;
      })
      .addCase(resetSearch, (state) => {
        state.companySearchResponse = initialState.companySearchResponse;
      })
      .addCase(resetCompanyById, (state) => {
        state.companyById = initialState.companyById;
      })
      .addCase(resetCompany, (state) => {
        state.companySearchResponse = initialState.companySearchResponse;
      });
  },
});

export default companySlice.reducer;
